import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './ReferralCode.css';


const sanitizeCode = (input) => input.trim().replace(/[^\w]/g, '');

const ReferralCode = ({ userDetails }) => {
  const [referralCode, setReferralCode] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeAssigned, setCodeAssigned] = useState(false);
  const [mode, setMode] = useState(null); // 'generate' | 'custom'

  const fetchReferralCode = useCallback(async () => {
    if (!userDetails?.email) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('https://sharedvision-partners.online/v1/API/referral.php', {
        params: { email: userDetails.email },
      });
      if (data?.referralCode) {
        setReferralCode(data.referralCode);
        setCodeAssigned(true);
        setMessage(data.message || 'Referral code retrieved.');
      } else {
        setCodeAssigned(false);
        setMessage(data.message || '');
      }
    } catch (err) {
      setError('Failed to fetch referral code.');
    } finally {
      setLoading(false);
    }
  }, [userDetails?.email]);

  const saveReferralCode = async (code) => {
    if (!userDetails?.email) return;
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post('https://sharedvision-partners.online/v1/API/referral.php', {
        email: userDetails.email,
        referralCode: code,
        source: mode, // Pass mode to backend ('generate' or 'custom')
      });
      if (data.success) {
        setReferralCode(data.referralCode);
        setCodeAssigned(true);
        setMessage(data.message || 'Referral code saved successfully!');
      } else {
        setError(data.message || 'Error saving referral code.');
      }
    } catch (err) {
      setError('Failed to save referral code.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    if (mode !== 'generate' || !userDetails?.email || loading) return;
    // For generate mode, frontend just sends email and mode, backend generates the code.
    saveReferralCode(''); // sending empty code; backend will generate
  };

  const handleCustomSubmit = () => {
    if (mode !== 'custom' || loading) return;
    const sanitized = sanitizeCode(customInput);
    if (!sanitized) {
      setError('Custom code can only include letters, numbers, and underscores.');
      return;
    }
    // For custom mode, send the prefix only; backend will add random suffix.
    saveReferralCode(sanitized);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setCustomInput('');
    setError('');
    setMessage('');
  };

  useEffect(() => {
    if (userDetails?.email) fetchReferralCode();
  }, [fetchReferralCode, userDetails?.email]);

  return (
    <section className="referral-container" aria-labelledby="referral-title">
      <h3 id="referral-title" className="form-title">Referral Code Generator</h3>
      <p className="user-email">Referral for: <strong>{userDetails?.email}</strong></p>

      {error && <p className="error-message" role="alert">{error}</p>}
      {message && <p className="success-message">{message}</p>}
      {loading && <p className="loading-spinner" aria-live="polite">Processing...</p>}

      {codeAssigned ? (
        <>
          <p>Your referral code is: <strong>{referralCode}</strong></p>
          <p className="info-message">Give this to others to earn Coins </p>
        </>
      ) : (
        <div className="form-container">
          <div className="form-buttons" role="group" aria-label="Referral mode selection">
            <button
              className={`choice-btn ${mode === 'generate' ? 'active' : ''}`}
              onClick={() => handleModeChange('generate')}
              disabled={loading || mode === 'generate'}
            >
              Generate
            </button>
            <button
              className={`choice-btn ${mode === 'custom' ? 'active' : ''}`}
              onClick={() => handleModeChange('custom')}
              disabled={loading || mode === 'custom'}
            >
              Customize
            </button>
          </div>

          {mode === 'generate' && (
            <div className="generate-form">
              <p className="info-message">Click to generate a referral code.</p>
              <button
                onClick={handleGenerate}
                className="generate-btn"
                disabled={loading || mode !== 'generate'}
              >
                {loading ? 'Generating...' : 'Generate Referral Code'}
              </button>
            </div>
          )}

          {mode === 'custom' && (
            <div className="custom-form">
              <label htmlFor="custom-referral-input" className="sr-only">Enter custom referral code</label>
              <input
                id="custom-referral-input"
                className="referral-input"
                type="text"
                placeholder="Enter custom referral prefix"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                disabled={loading}
              />
              <p className="info-message">Allowed characters: letters, numbers, underscores.</p>
              <button
                onClick={handleCustomSubmit}
                className="generate-btn"
                disabled={loading || mode !== 'custom'}
              >
                {loading ? 'Submitting...' : 'Submit Custom Code'}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ReferralCode;
